import { useCallback } from "react";

import { useToast } from "@hightouch/htui";
import { useQueryClient } from "react-query";

import {
  AssetCommentThreadStatuses,
  type AssetCommentThreadStatus,
  type CommentThreadSubject,
} from "@hightouch/lib/llm/asset-comments/types";
import { useUser } from "src/contexts/user-context";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import {
  type AssetCommentThreadsBoolExp,
  type AssetCommentThreadsQuery,
  useAssetCommentThreadsQuery,
  useCreateAssetCommentMutation,
  useDeleteAssetCommentMutation,
  useDeleteAssetCommentThreadMutation,
  useReopenAssetCommentThreadMutation,
  useResolveAssetCommentThreadMutation,
  useSetAssetCommentThreadReadStateMutation,
  useUpdateAssetCommentMutation,
} from "src/graphql";

import { toCommentThreadsFilter, useCommentFile } from "./comment-file";
import type { CommentDraft } from "./comment-composer";
import {
  COMMENT_THREAD_LIST_QUERY_KEY,
  invalidateThreadLists as invalidateCachedThreadLists,
  patchCachedThread,
  restoreCachedThreads,
  type ThreadListSnapshot,
} from "./thread-cache";
import { useRelevantCommentThreadUsers } from "./use-relevant-asset-users";
import {
  hasAgentReplyInFlight,
  isManuallyMarkedUnread,
  isThreadUnread,
  toMentionInput,
  toThreadComments,
  type ReadableThread,
} from "./utils";

/**
 * How often an open comment surface refetches. Shared by the rail, the canvas
 * pins, and the header count so they can't drift apart on screen.
 */
export const COMMENT_POLL_MS = 10_000;

/**
 * The cadence while an agent owes a reply or is deciding whether to give one.
 *
 * An agent reply can be shorter-lived than the normal poll — one observed
 * locally was reserved and written 3.9s apart — so at 10s the answer appears
 * with no sign anything produced it. Matches the progress poll inside
 * `PendingComment`.
 */
export const AGENT_REPLY_POLL_MS = 3000;

/** Query-key roots a comment mutation can affect. */
export const COMMENT_QUERY_KEYS = [
  COMMENT_THREAD_LIST_QUERY_KEY,
  "OpenAssetCommentThreadCount",
  // The ad sidebar's activity log reads the same threads, and doesn't poll.
  "AdBriefAssetActivity",
];

/**
 * Refetch only the comment queries, for a comment write's `onSuccess`.
 *
 * Passing `onSuccess` deliberately overrides the global one in
 * `src/lib/react-query.ts`, which invalidates every active query on the page.
 * Commenting is a high-frequency action on a screen with a lot of expensive
 * queries mounted, so the default would refetch the whole brief on each
 * keystroke-length interaction.
 */
export function useInvalidateCommentQueries() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        COMMENT_QUERY_KEYS.includes(queryKey[0] as string),
    });
  }, [queryClient]);
}

/**
 * Threads on one subject, plus every action on them.
 *
 * Shared by every surface showing the same subject's threads — for ads, the
 * rail and the inline card on the canvas — so they render the same data and
 * mutate through the same handlers. The query key is derived from the subject,
 * so those surfaces read one cache entry rather than fetching twice.
 */
export const useCommentThreads = ({
  subject,
  filter,
  threads: loadedThreads,
  keepPreviousData = false,
  onThreadDeleted,
  isEnabled = true,
  watchAgentReplies = false,
}: {
  subject: CommentThreadSubject;
  /** Threads to load in place of the subject's own, for a list spanning subjects like a canvas's. */
  filter?: AssetCommentThreadsBoolExp;
  /** Threads the page already loaded: the hook fetches nothing and its actions look threads up here. */
  threads?: AssetCommentThreadsQuery["asset_comment_threads"];
  /** Keeps the last threads while a changed `filter` loads, so a list spanning subjects doesn't blank. */
  keepPreviousData?: boolean;
  /** Which thread went, so a caller only reacts to one it was showing. */
  onThreadDeleted?: (threadId: string) => void;
  /**
   * Whether the surface is actually showing. Polling makes this matter: a rail
   * hidden behind a slide-out panel is still mounted, and would otherwise
   * refetch the thread list every few seconds on a screen nobody is reading.
   */
  isEnabled?: boolean;
  /**
   * Whether this surface reads a thread closely enough to want an agent's reply
   * as it happens — an open card, not the rail or the pins.
   *
   * Opt-in because the surfaces share one query key and react-query polls a
   * shared entry at the shortest interval any mount asks for, so left on by
   * default an open card would speed the rail and pins up too.
   */
  watchAgentReplies?: boolean;
}) => {
  const { user } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const file = useCommentFile();
  const where = filter ?? toCommentThreadsFilter(subject, file);
  // A file's agent answers in any of its threads, so every surface on it keeps up.
  const watchesAgentReplies = watchAgentReplies || file?.hasAgent === true;
  // Comments arrive from other people, so the rail keeps up on its own.
  // react-query only polls a mounted query, and the governed interval leaves
  // the fetches to the focus manager, so a hidden tab costs nothing.
  //
  // No backoff — `maxMs` pinned to `baseMs`. Commenting is bursty: quiet for
  // ten minutes, then three replies. Backing off to 30s would put the delay at
  // its longest exactly when the conversation restarts. Jitter stays, to keep
  // several open rails off the same tick.
  const { refetchInterval } = useGovernedRefetchInterval({
    baseMs: COMMENT_POLL_MS,
    maxMs: COMMENT_POLL_MS,
  });

  const { data: queriedThreads, isLoading: isQuerying } =
    useAssetCommentThreadsQuery(
      { subject: where },
      {
        select: (data) => data.asset_comment_threads,
        enabled: isEnabled && loadedThreads === undefined,
        keepPreviousData,
        // Both arguments forwarded, so the governed hook can still tell a settled
        // fetch from a render-driven recompute.
        refetchInterval: (data, query) =>
          watchesAgentReplies && hasAgentReplyInFlight(data)
            ? AGENT_REPLY_POLL_MS
            : refetchInterval(data, query),
      },
    );

  const threads = loadedThreads ?? queriedThreads;
  const isLoading = loadedThreads === undefined && isQuerying;
  // By the threads shown rather than the subject, so a list spanning subjects, like a canvas's, names everyone on it.
  // Not gated on `isEnabled`, which only stops the thread poll: a surface reading threads through the shared cache, like
  // the board's, still names their authors. The names don't poll, and the same threads ask by the
  // same sorted ids, so every mount on them shares one cache entry.
  const relevantUsers = useRelevantCommentThreadUsers(
    (threads ?? []).map(({ id }) => id),
  );

  const invalidateCommentQueries = useInvalidateCommentQueries();

  const scopedInvalidation = { onSuccess: invalidateCommentQueries };

  /**
   * Narrower than {@link invalidateCommentQueries}: read state can't change the
   * open-thread count, so there's no reason to refetch that aggregate on every
   * thread opened.
   */
  const invalidateThreadLists = useCallback(() => {
    invalidateCachedThreadLists(queryClient);
  }, [queryClient]);

  /**
   * Resolving and reopening are optimistic, because settling a thread is a
   * request to be done with it: the rail hides resolved threads and the canvas
   * drops their pins, and both have to happen on the click rather than after a
   * write and its refetch. Left to the round trip, the thread you just checked
   * off sits there for a beat looking like the click missed.
   */
  const statusTransition = (nextStatus: AssetCommentThreadStatus) => ({
    onMutate: async ({ threadId }: { threadId: string }) => ({
      previous: await patchCachedThread(queryClient, threadId, (thread) => ({
        ...thread,
        current_status: nextStatus,
      })),
    }),
    onError: (
      _error: unknown,
      _variables: { threadId: string },
      context: { previous: ThreadListSnapshot } | undefined,
    ) => {
      restoreCachedThreads(queryClient, context?.previous);
      invalidateCommentQueries();
    },
    // Must be `onSuccess` rather than `onSettled`: only a mutation's own
    // `onSuccess` overrides the global one, which refetches the whole page.
    onSuccess: invalidateCommentQueries,
  });

  const createComment = useCreateAssetCommentMutation(scopedInvalidation);
  const updateComment = useUpdateAssetCommentMutation(scopedInvalidation);
  const resolveThread = useResolveAssetCommentThreadMutation(
    statusTransition(AssetCommentThreadStatuses.Resolved),
  );
  const reopenThread = useReopenAssetCommentThreadMutation(
    statusTransition(AssetCommentThreadStatuses.Open),
  );
  const deleteThread = useDeleteAssetCommentThreadMutation(scopedInvalidation);
  const deleteComment = useDeleteAssetCommentMutation(scopedInvalidation);

  /**
   * Optimistic, because the dot has to stop being a dot on the click that
   * clears it — waiting a poll for the server to agree would leave it up for
   * seconds. `onMutate` writes the expected state and returns the previous one,
   * `onError` puts it back, and `onSettled` invalidates so the server's version
   * is what the rail ends up rendering.
   */
  const setReadState = useSetAssetCommentThreadReadStateMutation({
    onMutate: async ({ threadId, readThrough, manuallyMarkedUnreadAt }) => ({
      previous: await patchCachedThread(queryClient, threadId, (thread) => ({
        ...thread,
        reads: [
          {
            last_read_comment_at: readThrough,
            manually_marked_unread_at: manuallyMarkedUnreadAt ?? null,
          },
        ],
      })),
    }),
    onError: (_error, _variables, context) => {
      restoreCachedThreads(queryClient, context?.previous);
      invalidateThreadLists();
    },
    // Must be `onSuccess`, not `onSettled`: the global default in
    // `src/lib/react-query.ts` is only overridden by a mutation's own
    // `onSuccess`, and it invalidates every active query on the page.
    onSuccess: invalidateThreadLists,
  });
  const { mutateAsync: setReadStateAsync } = setReadState;

  const notifyFailure = (id: string, title: string, error: unknown) => {
    toast({
      id,
      title,
      message: error instanceof Error ? error.message : undefined,
      variant: "error",
    });
  };

  const reply = async (threadId: string, draft: CommentDraft) => {
    try {
      await createComment.mutateAsync({
        threadId,
        body: draft.body,
        mentions: draft.mentions.map(toMentionInput),
        version: null,
      });
    } catch (error) {
      notifyFailure("asset-comment-reply", "Couldn't post your reply", error);
    }
  };

  /**
   * Rewrites one of the viewer's own comments, reporting whether it saved so the
   * surface can keep the box — and the text in it — when it didn't.
   *
   * The mentions ride along with the body rather than being re-derived from it:
   * the service checks the two agree, which is what stops a composer bug from
   * writing notification rows the body doesn't support.
   */
  const editComment = async (
    commentId: string,
    draft: CommentDraft,
  ): Promise<boolean> => {
    try {
      await updateComment.mutateAsync({
        commentId,
        body: draft.body,
        mentions: draft.mentions.map(toMentionInput),
      });

      return true;
    } catch (error) {
      notifyFailure("asset-comment-edit", "Couldn't save your changes", error);

      return false;
    }
  };

  const toggleResolved = async (threadId: string, isResolved: boolean) => {
    try {
      if (isResolved) {
        await reopenThread.mutateAsync({ threadId });
      } else {
        await resolveThread.mutateAsync({ threadId });
      }
    } catch (error) {
      notifyFailure(
        "asset-comment-resolve",
        isResolved
          ? "Couldn't reopen this thread"
          : "Couldn't resolve this thread",
        error,
      );
    }
  };

  /**
   * Deletes the thread and every comment in it.
   */
  const removeThread = async (threadId: string) => {
    try {
      await deleteThread.mutateAsync({ threadId });
      onThreadDeleted?.(threadId);
    } catch (error) {
      notifyFailure(
        "asset-comment-delete",
        "Couldn't delete this thread",
        error,
      );
    }
  };

  /**
   * Deletes a comment.
   *
   * Deleting the last one deletes the thread instead: an empty thread would
   * still render a pin on the canvas with nothing behind it.
   */
  const removeComment = async (threadId: string, commentId: string) => {
    const remaining = threads?.find((thread) => thread.id === threadId)
      ?.comments.length;

    if (remaining === 1) {
      await removeThread(threadId);
      return;
    }

    try {
      await deleteComment.mutateAsync({ commentId });
    } catch (error) {
      notifyFailure(
        "asset-comment-delete",
        "Couldn't delete this comment",
        error,
      );
    }
  };

  /**
   * Clears the unread marker on a thread the viewer just opened, including one
   * they marked unread by hand — opening it is that manual clear. Leaving it
   * set would strand the thread unread until they found the menu item.
   *
   * Fails silently: a toast on every thread clicked would be worse than the
   * marker staying put, and the next open retries.
   */
  const markReadOnOpen = async (threadId: string) => {
    const thread = threads?.find((candidate) => candidate.id === threadId);

    if (!thread) {
      return;
    }

    // The comment's timestamp rather than now(), so a comment landing between
    // the render and the click stays unread.
    const readThrough = thread.last_comment_at ?? thread.created_at;

    if (
      !isManuallyMarkedUnread(thread) &&
      thread.reads[0]?.last_read_comment_at === readThrough
    ) {
      return;
    }

    try {
      await setReadStateAsync({
        threadId,
        readThrough,
        manuallyMarkedUnreadAt: null,
      });
    } catch {
      // Rolled back by the mutation's onError; the next open retries.
    }
  };

  /** Marks a thread read or unread by hand, from the card's action. */
  const setThreadRead = async (threadId: string, isRead: boolean) => {
    const thread = threads?.find((candidate) => candidate.id === threadId);

    if (!thread) {
      return;
    }

    const readThrough = isRead
      ? (thread.last_comment_at ?? thread.created_at)
      : (thread.reads[0]?.last_read_comment_at ?? thread.created_at);
    const manuallyMarkedUnreadAt = isRead ? null : new Date().toISOString();

    try {
      await setReadStateAsync({
        threadId,
        readThrough,
        manuallyMarkedUnreadAt,
      });
    } catch (error) {
      notifyFailure(
        "asset-comment-read-state",
        isRead ? "Couldn't mark as read" : "Couldn't mark as unread",
        error,
      );
    }
  };

  return {
    threads,
    isLoading,
    currentUserId: user?.id,
    markReadOnOpen,
    setThreadRead,
    /**
     * Bound to the viewer's join date, so a new arrival doesn't find every
     * thread in the workspace marked unread.
     */
    isUnread: (thread: ReadableThread) =>
      isThreadUnread(thread, user?.created_at),
    /** For callers that filter rather than badge — the two must agree. */
    joinedAt: user?.created_at,
    toComments: (thread: NonNullable<typeof threads>[number]) =>
      toThreadComments(thread.comments, user?.id, relevantUsers),
    reply,
    editComment,
    toggleResolved,
    removeComment,
    removeThread,
    isReplying: createComment.isLoading,
    isSavingEdit: updateComment.isLoading,
  };
};
